import type { ReactNode } from 'react';
import { Button, Close, Modal } from '../index';

export interface DisclaimerModalProps {
  onClose: () => void;
  title?: string;
  description: ReactNode;
  dismissLabel?: string;
  ariaLabelledBy?: string;
}

export function DisclaimerModal({
  onClose,
  title = 'Disclaimer',
  description,
  dismissLabel = 'OK',
  ariaLabelledBy,
}: DisclaimerModalProps) {
  return (
    <Modal
      onClose={onClose}
      inspectName="DisclaimerModal"
      widthClassName="max-w-[400px]"
      {...(ariaLabelledBy ? { ariaLabelledBy } : {})}
      closeSlot={
        /* Close (X) — sits OUTSIDE the white sheet, to the right of it
           on ≥640 px (matches Figma 706:1666 — round dark pill peeking
           out next to the card). On mobile it overlays the top-right
           corner of the viewport so it stays reachable on narrow widths.
           Through `closeSlot` so the card's entrance transform never becomes
           the containing block for that `fixed`.

           Shared DS `glass-frosted` Button so theming flows through the
           global `--co-Button-glass-frosted*` knobs (same tokens that paint
           every other frosted pill in the app). The className overrides
           cinch the pill into a square 36×36 round tile with no horizontal
           padding so the icon sits centred. */
        <Button
          variant="glass-frosted"
          aria-label="Close"
          onClick={onClose}
          className="
            fixed! top-[calc(1rem+var(--page-safe-top))]! right-4! z-60!
            sm:absolute! sm:top-0! sm:right-0! sm:z-10!
            sm:translate-x-[calc(100%+12px)]! sm:translate-y-0!
            h-9! w-9! px-0! gap-0! rounded-full!
          "
          leftIcon={<Close size={16} strokeWidth={2} />}
        >
          {null}
        </Button>
      }
    >
      <div className="flex flex-col items-stretch">
        {/* White sheet — the disclaimer card. Tagged so the DC inspector
            opens the single DisclaimerModal panel from any empty area. */}
        <section
          {...(ariaLabelledBy ? { id: ariaLabelledBy } : {})}
          data-co-component="DisclaimerModal"
          className="flex flex-col gap-4 px-6 py-6"
          style={{
            backgroundColor: 'var(--co-DisclaimerModal-cardFill, #FFFFFF)',
            borderRadius: 'var(--co-DisclaimerModal-borderRadius, 24px)',
          }}
        >
          {title && (
            <h2
              className="m-0"
              style={{
                color: 'var(--co-DisclaimerModal-titleColor, #15181E)',
                fontFamily:
                  'var(--co-DisclaimerModal-titleFont, "Inter"), Inter, sans-serif',
                fontSize: 'var(--co-DisclaimerModal-titleSize, 18px)',
                fontWeight: 700,
                letterSpacing: '-0.3px',
                lineHeight: 1.25,
              }}
            >
              {title}
            </h2>
          )}

          <p
            className="m-0"
            style={{
              color: 'var(--co-DisclaimerModal-descColor, rgba(0,0,0,0.78))',
              fontFamily:
                'var(--co-DisclaimerModal-descFont, "Inter"), Inter, sans-serif',
              fontSize: 'var(--co-DisclaimerModal-descSize, 15px)',
              fontWeight: 400,
              lineHeight: 1.45,
            }}
          >
            {description}
          </p>

          {/* Dismiss button — outline pill, white fill + soft grey border
              + dark text (matches Figma 706:1666). Themable via the
              dedicated `dismiss*` knobs on this surface so the operator
              can retint without touching the global Button tokens. */}
          <button
            type="button"
            onClick={onClose}
            className="
              mt-2 inline-flex h-12 w-full items-center justify-center
              transition-opacity hover:opacity-80 active:scale-[0.99]
            "
            style={{
              backgroundColor: 'var(--co-DisclaimerModal-dismissFill, #FFFFFF)',
              color: 'var(--co-DisclaimerModal-dismissText, #15181E)',
              border: '1px solid var(--co-DisclaimerModal-dismissBorder, rgba(0,0,0,0.12))',
              borderRadius: 'var(--co-DisclaimerModal-dismissBorderRadius, 999px)',
              fontFamily:
                'var(--co-DisclaimerModal-dismissFont, "Inter"), Inter, sans-serif',
              fontSize: 'var(--co-DisclaimerModal-dismissSize, 15px)',
              fontWeight: 600,
            }}
          >
            {dismissLabel}
          </button>
        </section>
      </div>
    </Modal>
  );
}
